import { Drawer } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle, Cpu, Paperclip, Search, X } from "react-feather";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Card,
  CardContent,
  Menu,
  Dropdown as SemanticDropdown,
} from "semantic-ui-react"; // Add alias to avoid naming conflicts
import {
  getBulkDropdownData,
  getDropdownData,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import {
  getRoute,
  processRequest,
  searchRequest,
} from "../../api/requestApprovalApi";
import { statusByContext } from "../../api/statusApi";
import Grid from "../../components/Grid";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import Input from "../../components/input/Input";
import SearchDropdown from "../../components/input/SearchDropdown";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import RequestCreationRefactor from "../../components/transaction/RequestCreation/RequestCreationRefactor";
import { requestApprovalValidation } from "../../components/validations/requestApproval";
import { FORMS, MODULES, STATUS_GROUPS, statusById } from "../../const";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";

const FORM = FORMS.REQUEST_APPROVAL.APPROVAL.form;

function RequestApproval() {
  const userData = useSelector((state) => state.user.userDetails);
  const user = useSelector((state) => state.user);
  const auth = useSelector(
    (state) => state.user.permissions[MODULES.Transaction],
  );
  const requestApprovalPermission = auth[FORMS.REQUEST_APPROVAL.APPROVAL.form];
  const downloadPermission = requestApprovalPermission?.FORM?.DOWNLOAD;
  const [tab, setTab] = useState(1);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const gridRef = useRef();
  const [selectedRows, setSelectedRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [isResetClicked, setIsResetClicked] = useState(false);

  const [selectedValue, setSelectedValue] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRequestCode, setSelectedRequestCode] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const userCompany = useUserCompany();

  const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null,
  );

  useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
  }, [userCompany]);

  useEffect(() => {
    if (Object.keys(errors).length !== 0) {
      // Add error class to the form for styling
      document.querySelector(".ui.form").classList.add("error");
    } else {
      document.querySelector(".ui.form").classList.remove("error");
    }
  }, [errors]);

  // Dropdown Data
  const [dateTypeOptions, setDateTypeOptions] = useState([]);
  const [statusTypeOptions, setStatusTypeOptions] = useState([]);
  const [authStatusTypeOpt, setAuthStatusTypeOpt] = useState([]);
  const [shipmentTypeOptions, setShipmentTypeOptions] = useState([]);
  const [yesNoOptions, setYesNoOptions] = useState([]);
  const [remarkCategoryOptions, setRemarkCategoryOptions] = useState([]);
  const [chargeTypeOptions, setChargeTypeOptions] = useState([]);
  const [routeTypeOptions, setRouteTypeOptions] = useState([]);
  const [jobTypeOptions, setJobTypeOptions] = useState([]);
  const [kagNumberOptions, setKagNumberOptions] = useState([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [requestViewMode, setRequestViewMode] = useState(true);
  const [requestEditModeEnabledIds, setRequestEditModeEnabledIds] = useState(
    [],
  );

  const [currentErrors, setCurrentErrors] = useState({});

  const [dateType, setDateType] = useState(1);
  const [searchStatus, setSearchStatus] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerIds, setCustomerIds] = useState([]);
  const [requestCode, setRequestCode] = useState("");
  const [shippers, setShippers] = useState([]);
  const [shipmentType, setShipmentType] = useState("");
  const [scheduler, setScheduler] = useState(2);

  const [remarkCategory, setRemarkCategory] = useState("");
  const [remarks, setRemarks] = useState("");
  const [status, setStatus] = useState(1);
  const [chargeType, setChargeType] = useState(5);
  const [routeType, setRouteType] = useState(1);
  const [jobType, setJobType] = useState(1);
  const [noOfJobs, setNoOfJobs] = useState(1);
  const [kagNo, setKagNo] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [dataUpdated, setDataUpdated] = useState(false);
  const handleReset = (e) => {
    e.preventDefault();
    setIsResetClicked(!isResetClicked);
    setSelectedRows([]);
    setDateType(1);
    setFromDate("");
    setToDate("");
    setSearchStatus(1);
    setCustomerIds([]);
    setRequestCode("");
    setShippers([]);
    setSearchFieldReset(true);
    setShipmentType("");
    setKagNo("");
    setSearchClicked(false);
    setResetVisible(false);
    // setActiveAccordion1(false);
    setSearchResults([]);
    setScheduler(2);

    setErrors({});
    setStatus(1);
    setRemarkCategory("");
    setRemarks("");
    setChargeType(5);
    setRouteType(1);
    setJobType(1);
    setNoOfJobs(1);
  };

  useEffect(() => {
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0];
    setToDate(formattedToday);
    const twoDaysAgo = new Date(today.setDate(today.getDate() - 2));
    const formattedDate = twoDaysAgo.toISOString().split("T")[0];
    setFromDate(formattedDate);
  }, [isResetClicked]); // The empty dependency array ensures this effect runs only once when the component mounts

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const statusOpt = await getDropdownData("status");

      const { status: statusSearch } = await getBulkDropdownData([
        {
          key: "status",
          authenticated: true,
          authorities: [
            "REQUEST_APPROVAL_DROPDOWN_APPROVE_VIEW",
            "REQUEST_APPROVAL_DROPDOWN_PENDING_VIEW",
            "REQUEST_APPROVAL_DROPDOWN_REJECT_VIEW",
          ],
        },
      ]);

      setStatusTypeOptions(statusSearch);

      //new
      const {
        date_types: dateTypeData,
        yesno: yesNoOpt,
        status: statusTypeData,
        shipment_type: shipmentType,
        approver_remark: remarkCategory,
        "vpy_type#2": chargeType,
        job_types: jobType,
        kag_number: kagTypeData,
      } = await getBulkDropdownData([
        { key: "date_types" },
        { key: "yesno" },
        {
          key: "status",
          authenticated: true,
          authorities: [
            "REQUEST_APPROVAL_FORM_APPROVE",
            "REQUEST_APPROVAL_FORM_REJECT",
          ],
        },
        { key: "shipment_type" },
        { key: "approver_remark" },
        { key: "vpy_type", group: "2" },
        { key: "job_types" },
        { key: "kag_number" },
      ]);

      setDateTypeOptions(dateTypeData);
      setYesNoOptions(yesNoOpt);
      // setStatusTypeOptions(statusOpt);
      setAuthStatusTypeOpt(statusTypeData);
      setShipmentTypeOptions(shipmentType);
      setRemarkCategoryOptions(remarkCategory);
      setChargeTypeOptions(chargeType);
      setJobTypeOptions(jobType);
      setKagNumberOptions(kagTypeData);

      const routes = await getRoute();
      setRouteTypeOptions(routes);

      const requestEditEnableList = await statusByContext(
        STATUS_GROUPS.REQUEST_VIEW,
      );
      const mappedREEList = requestEditEnableList.map((sta) => sta.key);
      setRequestEditModeEnabledIds(mappedREEList);

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    setErrors({});
    setStatus(1);
    setRemarkCategory("");
    setRemarks("");
    setChargeType(5);
    setRouteType(1);
    setJobType(1);
    setNoOfJobs(1);
  }, [selectedRows, searchResults]);

  const handleSearch = async (e) => {
    e.preventDefault();
    searchRequests();
  };

  const searchRequests = useCallback(async () => {
    setSelectedRows([]);
    setSearchResults([]);
    setSearchSubmitLoading(true);
    setSearchClicked(true);
    setResetVisible(true);
    setActiveAccordion1(false);

    const data = {
      dateType,
      fromDate,
      toDate,
      status: searchStatus,
      customerId: customerIds.join(","),
      requestCode: requestCode ? requestCode.toUpperCase() : null,
      shipper: shippers.join(","),
      shipmentType,
      kagNo,
      scheduler,
      user: user.username,
    };

    const searchRes = await searchRequest(data);

    const dateFormattedSearchRes = searchRes?.map((item) => ({
      ...item,
      reqCreatedDate: item.reqCreatedDate
        ? formatFromJsonDate(item.reqCreatedDate)
        : "",
      reqBookingRequiredDate: item.reqBookingRequiredDate
        ? formatFromJsonDate(item.reqBookingRequiredDate)
        : "",
      arrivalEta: item.arrivalEta ? formatFromJsonDate(item.arrivalEta) : "",
      reqDocCount: item.reqDocCount ? item.reqDocCount : 0,
      hasReqDocs: item.reqDocCount ? item.reqDocCount > 0 : false,
    }));

    const mappedResponse = dateFormattedSearchRes?.map((item) => ({
      ...item,
      viaLocation:
        item.viaLocation || item.viaLocation !== null
          ? item.viaLocation
          : "N/A",
      status: statusById[item.reqStatus],
    }));
    setSearchResults(mappedResponse);

    setSearchSubmitLoading(false);
  }, [
    customerIds,
    fromDate,
    searchStatus,
    toDate,
    requestCode,
    shippers,
    shipmentType,
    kagNo,
    dateType,
    scheduler,
  ]);

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current.getSelectedData();
    setSelectedRows(selectedData);
  }, []);

  const resetActionForm = (e) => {
    e.preventDefault();

    setErrors({});
    setStatus(1);
    setRemarkCategory("");
    setRemarks("");
    setChargeType(5);
    setRouteType(1);
    setJobType(1);
    setNoOfJobs(1);
  };

  console.log("selected rows", selectedRows);

  const requestProcess = async (e) => {
    e.preventDefault();

    setIsProcessing(true);
    let validation = {};
    if (status !== 3) {
      validation = requestApprovalValidation({
        status,
        // chargeType,
        route: routeType,
        // jobType,
        // numOfJobs: noOfJobs,
      });
    } else {
      validation = requestApprovalValidation({
        status,
        remark: remarkCategory,
        remarkDesc: remarks,
      });
    }

    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      setIsProcessing(false);
      toast.error("Please fill in all required fields!", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    } else {
      setErrors({});
      try {
        const selectedRequestIds = selectedRows?.map((item) => item.reqId);
        console.log("selectedRequestIds", selectedRequestIds);
        if (selectedRequestIds?.length === 0) {
          toast.error("Please select a request to process!", {
            autoClose: 5000,
            containerId: "Main",
          });
          setIsProcessing(false);
          return;
        } else {
          for (let reqId of selectedRequestIds) {
            // let data = {
            //   requestId: reqId,
            //   status,
            //   remark: remarkCategory,
            //   remarkDesc: remarks,
            //   chargeType,
            //   route: routeType,
            //   jobType,
            //   numOfJobs: status === 3 ? 0 : noOfJobs,
            //   createdBy: user.username,
            // };

            let data = {
              requestId: reqId,
              status,
              remark: remarkCategory,
              remarkDesc: remarks,
              chargeType: 5,
              route: 1,
              jobType: 1,
              numOfJobs: status === 3 ? 0 : noOfJobs,
              company: company,
              createdBy: user.username,
            };

            const request = selectedRows?.find(
              (request) => request.reqId === reqId,
            );

            const res = await processRequest(data);
            console.log("request approval response", res);

            if (res.status === 200) {
              if (status === 2) {
                for (let i = 0; i < res.data.length; i++) {
                  const item = res.data[i];

                  if (item.jobRequest === -2) {
                    toast.error(item.jobCustomerBase, {
                      autoClose: 5000,
                      containerId: "Main",
                    });
                    setIsProcessing(false);
                    return;
                  }

                  if (item.jobRequest === -1) {
                    toast.error(item.jobCustomerBase, {
                      autoClose: 5000,
                      containerId: "Main",
                    });
                    setIsProcessing(false);
                    return;
                  }

                  if (i === 0) {
                    toast.success(
                      <div>Request with ID {request.reqCode} Approved..!</div>,
                      { autoClose: 5000, containerId: "Main" },
                    );
                  }

                  toast.success(<div>Job Code {item.jobCode} Created..</div>, {
                    autoClose: 5000,
                    containerId: "Main",
                  });
                }
              } else if (status === 3) {
                toast.success(
                  <div>Request with ID {request.reqCode} Rejected..!</div>,
                  { autoClose: 5000, containerId: "Main" },
                );
              }
            } else {
              toast.error("An error occurred..", {
                autoClose: 5000,
                containerId: "Main",
              });
            }
          }

          setDataUpdated(true);
          setSelectedRows([]);

          setErrors({});
          setStatus(1);
          setRemarkCategory("");
          setRemarks("");
          setChargeType(5);
          setRouteType(1);
          setJobType(1);
          setNoOfJobs(1);
        }
      } catch (e) {
        console.log(e);
        toast.error("Error processing request!", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
    }

    setIsProcessing(false);
  };

  useEffect(() => {
    if (dataUpdated) {
      searchRequests();
      setDataUpdated(false);
    }
  }, [dataUpdated, searchRequests]);

  const getDropdownOptions = () => {
    const options = [
      {
        key: 0,
        text: "Select Action",
        value: 0,
      },
      {
        key: 2,
        text: "Edit",
        value: 2,
        ...(selectedRows.length !== 1 && { disabled: true }),
      },
    ];
    if (downloadPermission) {
      options.push({
        key: 1,
        text: "Download",
        value: 1,
        ...(selectedRows.length === 0 && { disabled: true }),
      });
    }
    return options;
  };
  const options = getDropdownOptions();

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleDropdownChange = (e, data) => {
    setSelectedValue(data.value);
    if (data.value === 1) {
      gridRef.current.download();
    } else if (data.value === 2) {
      console.log("sts_sta_id", selectedRows[0].stsStaId);
      console.log("requestEdit mode enabled", requestEditModeEnabledIds);
      setRequestViewMode(
        selectedRows[0].reqStatus === 2 &&
          !requestEditModeEnabledIds.includes(selectedRows[0].stsStaId),
      );
      setSelectedRequestCode(selectedRows[0].reqCode);
      setSelectedRequestId(selectedRows[0].reqId);
      handleDrawerOpen();
    }

    setSelectedValue(0);
  };
  return (
    <>
      <Card style={{ width: "100%" }}>
        <CardContent style={{ width: "100%" }}>
          <div
            className="ui attached tabular"
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div className="ui attached tabular menu">
              <div className="ui tabular">
                <div
                  className={`item ${tab === 1 ? "active" : ""}`}
                  onClick={() => {
                    setTab(1);
                  }}
                >
                  <span>
                    <CheckCircle size={15} style={{ marginRight: "5px" }} />
                  </span>
                  Request Approval Search
                </div>
              </div>
            </div>
            {selectedRows.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  height: "30px",
                }}
              >
                <Menu compact>
                  <SemanticDropdown
                    text="Select Action"
                    options={options}
                    simple
                    item
                    onChange={handleDropdownChange}
                    value={selectedValue}
                    style={{ width: "138px" }}
                  />
                </Menu>
              </div>
            )}
          </div>
          <div
            className="ui bottom attached active tab segment"
            style={{ display: tab === 1 ? "block" : "none" }}
          >
            <div className="ui styled accordion" style={{ width: "100%" }}>
              <div
                className={`title ${activeAccordion1 ? "active" : ""}`}
                onClick={() => setActiveAccordion1(!activeAccordion1)}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span>Search</span>
                    <Search size={14} style={{ marginLeft: "10px" }} />
                    <i className="dropdown icon"></i>
                  </div>

                  <i
                    className="refresh icon primary button"
                    style={{
                      display: `${resetVisible ? "flex" : "none"}`,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      handleReset(e);
                    }}
                  ></i>
                </div>
              </div>
              <div className={`content ${activeAccordion1 ? "active" : ""}`}>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <form className="ui form">
                    <h4 className="ui dividing header">Request Approval</h4>
                    <div className="four fields ">
                      <Dropdown
                        label="Date Type"
                        error={currentErrors.dateType}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_date_type"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { dateType: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        placeholder="Date Type"
                        options={dateTypeOptions}
                        value={dateType}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setDateType(data.value);
                        }}
                      />
                      <Input
                        label="From Date"
                        error={currentErrors.fromDate}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_from_date"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { fromDate: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        type="date"
                        name="from_date"
                        placeholder="From Date"
                        value={fromDate}
                        onChange={(e) => {
                          setFromDate(e.target.value);
                        }}
                      />
                      <Input
                        label="To Date"
                        error={currentErrors.toDate}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_to_date"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { toDate: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        type="date"
                        name="to_date"
                        placeholder="To Date"
                        value={toDate}
                        onChange={(e) => {
                          setToDate(e.target.value);
                        }}
                        min={fromDate}
                        disabled={!fromDate}
                      />
                      <Input
                          label="Request Codes"
                          error={currentErrors.requestCode}
                          form={FORM}
                          module={MODULES.Transaction}
                          field="rap_approval_request_code"
                          resetError={() =>
                              setCurrentErrors((prevState) => {
                                const { requestCode: _, ...rest } = prevState;
                                return rest;
                              })
                          }
                          name="reqCodes"
                          placeholder="Example - REQ-00001,REQ-00002, ..."
                          value={requestCode}
                          onChange={(e) => setRequestCode(e.target.value)}
                      />
                    </div>
                    <div className="four fields ">
                      <Dropdown
                        label="Shipment Type"
                        error={currentErrors.shipmentType}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_shipment_type"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { shipmentType: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        placeholder="Shipment Type"
                        options={shipmentTypeOptions}
                        value={shipmentType}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setShipmentType(data.value);
                        }}
                      />
                      <Dropdown
                          label="KAG Number"
                          clearable={true}
                          error={currentErrors.kagNo}
                          form={FORM}
                          module={MODULES.Transaction}
                          field="rap_kag_number"
                          resetError={() =>
                              setCurrentErrors((prevState) => {
                                const { kagNo: _, ...rest } = prevState;
                                return rest;
                              })
                          }
                          placeholder="Select KAG Number"
                          options={kagNumberOptions}
                          value={kagNo}
                          style={{ height: "5px" }}
                          onChange={(e, data) => {
                            setKagNo(data.value);
                          }}
                      />
                      <Dropdown
                        label="Status"
                        clearable={false}
                        error={currentErrors.searchStatus}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_search_status"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { searchStatus: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        placeholder="Status"
                        options={statusTypeOptions}
                        value={searchStatus}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setSearchStatus(data.value);
                        }}
                      />
                      <Dropdown
                        label="Scheduler"
                        clearable={false}
                        error={currentErrors.scheduler}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_search_scheduler"
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { scheduler: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        placeholder="Scheduler"
                        options={yesNoOptions}
                        value={scheduler}
                        style={{ height: "5px" }}
                        onChange={(e, data) => {
                          setScheduler(data.value);
                        }}
                      />
                    </div>
                    <div className="two fields">
                      <SearchDropdown
                        key={1}
                        editMode={false}
                        reset={searchFieldReset}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_customer"
                        setReset={setSearchFieldReset}
                        onResultSelect={(data) =>
                          setCustomerIds((prev) => [...prev, data])
                        }
                        api={getOtherRefSearchDropdown}
                        apiDependency={
                          userData.type === 1
                            ? {
                                type: "stakeholder_type",
                                value: "Customer",
                              }
                            : {
                                type: "user_stakeholder",
                                value: "Requesting Customer",
                                param2: user.username,
                              }
                        }
                        multiple={true}
                        multiValueRemoveHandler={(key) => {
                          setCustomerIds((prev) =>
                            prev.filter((item) => item !== parseInt(key)),
                          );
                        }}
                        label="Customer"
                      />

                      <SearchDropdown
                        key={1}
                        editMode={false}
                        reset={searchFieldReset}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="rap_approval_shipper"
                        setReset={setSearchFieldReset}
                        onResultSelect={(data) =>
                          setShippers((prev) => [...prev, data])
                        }
                        api={getOtherRefSearchDropdown}
                        apiDependency={
                          userData.type === 1
                            ? {
                                type: "stakeholder_type",
                                value: "Shipper",
                                param2:
                                  customerIds.length > 0
                                    ? customerIds.join(",")
                                    : "",
                              }
                            : {
                                type: "user_stakeholder",
                                value: "Shipper",
                                param1:
                                  customerIds.length > 0
                                    ? customerIds.join(",")
                                    : "",
                                param2: user.username,
                              }
                        }
                        multiple={true}
                        multiValueRemoveHandler={(key) => {
                          console.log(key);
                          setShippers((prev) =>
                            prev.filter((item) => item !== parseInt(key)),
                          );
                        }}
                        label="Shipper"
                      />
                    </div>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <div className="field">
                        <Button
                          template="search"
                          onClick={(e) => {
                            handleSearch(e);
                          }}
                        />
                        <Button
                          template="searchReset"
                          onClick={(e) => {
                            handleReset(e);
                          }}
                        />
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
            {searchClicked && (
              <>
                {searchSubmitLoading ? (
                  <LoadingSpinner />
                ) : (
                  <Grid
                    ref={gridRef}
                    storageKey={`aggrid:request-approval}`}
                    persistColumnConfig={true}
                    content={searchResults}
                    multiRowSelection={true}
                    downloadParam={""}
                    downloadAction="Request Approval Excel Download"
                    onSelectionChanged={handleSelectionChanged}
                    cellSizeStrategy="fitCellContents"
                    fitContentCellls={[
                      "customerName",
                      "startLocation",
                      "gReqPlant",
                      "endLocation",
                      "createdBy",
                      "reqContactPerson",
                    ]}
                    config={[
                      {
                        headerName: "Request Code",
                        field: "reqCode",
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                        pinned: "left",
                        width: 200,
                        cellRenderer: (params) => {
                          return (
                            <div
                              style={{
                                // color: params.color,
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <span>{params.value}</span>
                              {params.hasDocs && (
                                <div className="header-attachment-icon">
                                  <Paperclip size={17} className="attachment" />
                                  <label className="attachment-count">
                                    {params.docCount > 100
                                      ? "99+"
                                      : params.docCount}
                                  </label>
                                </div>
                              )}
                            </div>
                          );
                        },
                        cellRendererParams: (params) => ({
                          docCount: params.data.reqDocCount,
                          hasDocs: params.data.hasReqDocs,
                        }),
                      },
                      {
                        headerName: "Request Created Date",
                        field: "reqCreatedDate",
                        minWidth: 250,
                      },
                      {
                        headerName: "Customer Name",
                        field: "customerName",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Start Location",
                        field: "startLocation",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "End Location",
                        field: "endLocation",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Via Locations",
                        field: "viaLocation",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Arrival ETA",
                        field: "arrivalEta",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "KAG No",
                        field: "reqKagNoValue",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Creator Remark",
                        field: "reqRemarks",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      { headerName: "Created By", field: "createdBy" },
                      {
                        headerName: "Contact Person",
                        field: "reqContactPerson",
                      },
                      { headerName: "Shipment Type", field: "shipmentType" },
                      { headerName: "Vehicle Type", field: "vehicleType" },
                      { headerName: "Item Type", field: "itemType" },
                      {
                        headerName: "Plant/Shipper",
                        field: "plantName",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Consignment Type",
                        field: "consignmentType",
                      },
                      {
                        headerName: "Consignee/Buyer",
                        field: "appConsignee",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Other Request",
                        cellRenderer: ResizableScrollingTextRenderer,
                        field: "chargeType",
                      },
                      {
                        headerName: "Rejected By",
                        cellRenderer: ResizableScrollingTextRenderer,
                        field: "reqRejectedBy",
                      },
                      {
                        headerName: "Rejected Remark",
                        cellRenderer: ResizableScrollingTextRenderer,
                        field: "reqRejectRemark",
                      },

                      {
                        headerName: "Status",
                        field: "status",
                        cellRenderer: StatusCellStyle,
                        pinned: "right",
                      },
                    ]}
                  />
                )}
              </>
            )}
            {selectedRows.length > 0 && searchStatus !== 2 && (
              <form className="ui form segment">
                <h4 className="ui dividing header">Action</h4>
                <div className="three fields ">
                  <Dropdown
                    required={true}
                    label="Status"
                    error={currentErrors.status}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rap_approval_status"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { status: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Status"
                    options={authStatusTypeOpt}
                    value={status}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setStatus(data.value);
                      setRouteType(1);
                    }}
                    clearable={false}
                  />
                  <Dropdown
                    required={status === 3}
                    label="Remarks Category"
                    error={currentErrors.remark}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rap_approval_remark"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { remark: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Remarks Category"
                    options={remarkCategoryOptions}
                    value={
                      status === 2
                        ? remarkCategory
                        : status === 3
                          ? remarkCategory
                          : ""
                    }
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setRemarkCategory(data.value);
                      console.log("this is the remark category: ", data.value);
                    }}
                    disabled={status === 2 ? false : status !== 3}
                  />
                  <Input
                    required={status === 3}
                    label="Remarks"
                    error={currentErrors.remarkDesc}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rap_approval_remark_desc"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { remarkDesc: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    name="remarks"
                    placeholder="Remarks"
                    value={status === 2 ? remarks : status === 3 ? remarks : ""}
                    onChange={(e) => {
                      setRemarks(e.target.value);
                    }}
                    disabled={status === 2 ? false : status !== 3}
                  />
                </div>
                <div className="two fields ">
                  {/* <Dropdown
                    required={status === 2}
                    label="Charge Type"
                    error={currentErrors.chargeType}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rap_approval_charge_type"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { chargeType: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Charge Type"
                    options={chargeTypeOptions}
                    value={status === 2 ? chargeType : ""}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setChargeType(data.value);
                    }}
                    disabled={status !== 2}
                  /> */}
                  <Dropdown
                    required={status === 2}
                    label="Route"
                    clearable={false}
                    error={currentErrors.route}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rap_approval_route"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { route: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Route"
                    options={routeTypeOptions}
                    value={status === 2 ? routeType : ""}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setRouteType(data.value);
                    }}
                    disabled={status !== 2}
                  />
                </div>
                <div className="two fields">
                  {/* <Dropdown
                    required={status === 2}
                    label="Job"
                    error={currentErrors.jobType}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rap_approval_job_type"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { jobType: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Job"
                    options={jobTypeOptions}
                    value={status === 2 ? jobType : ""}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setJobType(data.value);
                    }}
                    disabled={status === 2 ? false : true}
                    clearable={false}
                  /> */}
                  {jobType === 2 && (
                    <Input
                      required={status === 2}
                      label="No of Jobs"
                      error={currentErrors.numOfJobs}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="rap_approval_num_of_jobs"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { numOfJobs: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      type="number"
                      name="no_of_jobs"
                      placeholder="No of Jobs"
                      value={status === 2 ? noOfJobs : ""}
                      onChange={(e) => {
                        setNoOfJobs(e.target.value);
                      }}
                      disabled={status === 2 ? false : true}
                      min={1}
                      max={10}
                      maxLength={2}
                    />
                  )}
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field">
                    <Button
                      className="ui primary right floated button"
                      disabled={
                        isProcessing
                          ? true
                          : status === 1
                            ? true
                            : searchStatus === 2
                              ? true
                              : status === 3 && searchStatus === 3
                      }
                      onClick={(e) => {
                        requestProcess(e);
                      }}
                      label="Process"
                      Icon={Cpu}
                      isLoading={isProcessing}
                    />
                    <Button
                      template="reset"
                      onClick={(e) => {
                        resetActionForm(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>
      <Drawer anchor="right" open={isDrawerOpen}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "5px",
            }}
          >
            <X
              size={30}
              onClick={handleDrawerClose}
              style={{
                curser: "pointer",
              }}
            />
          </div>
          <div className="drawer-content" style={{ width: "90vw" }}>
            <div className="ui segment">
              <h3 className="ui header">Request Code: {selectedRequestCode}</h3>
              <RequestCreationRefactor
                id={selectedRequestId}
                viewOnlyMode={requestViewMode}
                cleanUp={true}
                cleanUpFunction={() => {
                  handleDrawerClose();
                }}
                context={2}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default RequestApproval;
